/**
 * Agent run state.
 *
 * ORGANIZATION SCOPING
 * --------------------
 * An agent run executes against one organization's data, so its transcript,
 * artifacts and live SSE handle belong to that organization. This store used to
 * hold a single `Map<agentId, entry>`, which meant a run started in Org A stayed
 * on screen — mid-stream — after switching to Org B.
 *
 * Runs are now partitioned: {@link AgentRunState.runsByOrg} holds every
 * organization's entries and {@link AgentRunState.runs} publishes the active
 * organization's. Stream handlers close over the organization that started the
 * run, so a frame that arrives after a switch updates Org A's slice — where it
 * belongs — instead of whatever happens to be on screen. No frame is dropped and
 * no frame is misfiled; the run is simply invisible until the user returns.
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import toast from 'react-hot-toast';
import { streamAgentRun, reconnectAgentRun } from '@/lib/studio/sse';
import { apiPost } from '@/services/studio/client';
import { progressFromEvents } from '@/components/organisms/studio/AgentRunProgress';
import { useActivityStore } from './activityStore';
import { getQueryClient } from '@/dataProvider/reactQuery/context';
import {
  getActiveOrgId,
  isActiveOrg,
  readOrgSlice,
  registerOrgScopedState,
  writeOrgSlice,
  type OrgId,
} from '@/lib/org-scope';
import type { RunEvent, RunArtifact } from '@/services/studio/types';
import type { StreamHandle } from '@/lib/studio/sse';
import type { RunPhase } from '@/pages/agents/AgentRunTab';

const MAX_VISIBLE_EVENTS = 240;

// Tell the backend to actually stop the run. Awaited + error-surfaced (the old
// fire-and-forget `void apiPost` swallowed 404s/500s, so a failed stop looked like
// success while the run kept executing server-side).
function stopAgentRunOnServer(runId: string): void {
  void apiPost(`/api/agent-runs/${encodeURIComponent(runId)}/stop`, {}).catch((err) => {
    console.error('[agentRunStore] failed to stop run on server', err);
    toast.error('Could not stop the run on the server — it may still be running.');
  });
}
const PROGRESS_EVENT_TYPES = new Set([
  'started', 'step_start', 'step_progress', 'step_result',
  'artifact', 'done', 'error', 'cache_hit', 'rate_limited', 'progress',
]);

// ============================================================================
// Types
// ============================================================================

export interface AgentRunEntry {
  agentId: string;
  agentName: string;
  phase: RunPhase;
  events: RunEvent[];
  result: string;
  runId: string | null;
  artifacts: RunArtifact[];
  handle: StreamHandle | null;
  activityId: string | null;
  // Set when the user hit Stop before the run_id arrived via SSE. The 'started'
  // handler honors it the moment the id lands (see stopRun for why we can't just
  // abort the stream in that window).
  stopRequested?: boolean;
  // Token/cost consumption from the terminal `done` event (backend get_run_cost).
  usage?: RunUsage | null;
  // Why the run ended badly — the raw code off the terminal `error` frame
  // ("llm_budget_exceeded", "run_failed:TimeoutError"), or a transport-failure
  // message when the stream itself broke. Kept because the phase alone ('done')
  // cannot distinguish a finished run from a failed one, and a failed run that
  // saved no report has nothing else to show the user.
  errorMessage?: string | null;
}

export interface RunUsage {
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  llm_calls: number;
}

type AgentRunMap = Map<string, AgentRunEntry>;

interface AgentRunState {
  /** Every organization's runs, keyed by organization then agent id. Not rendered. */
  runsByOrg: Map<string, AgentRunMap>;

  /**
   * The active organization's runs. Everything that renders reads this; treat as
   * read-only.
   */
  runs: AgentRunMap;

  // Init entry with defaults — no-op if entry already exists (prevents clobbering active run on re-mount)
  ensureEntry(agentId: string, agentName: string): void;

  // Start SSE stream, create activity entry
  startRun(agentId: string, agentName: string, input: string, scope: Record<string, string>): void;

  // Reconnect to an already-running run's live SSE stream (browser refresh).
  // Seeds the entry to phase 'running' with the given runId, then resumes the
  // live stream into the same reducer startRun uses. No-op if a live handle is
  // already attached for this run.
  reconnectRun(agentId: string, agentName: string, runId: string): void;

  // Abort SSE, reset to configure, remove activity
  stopRun(agentId: string): void;

  // For AgentWorkspace: set phase when viewing a historical run
  setPhase(agentId: string, phase: RunPhase): void;

  // For AgentWorkspace: clear events when switching to historical run view
  setEvents(agentId: string, events: RunEvent[]): void;

  // Partial update — used by AgentRunTab effects (persistedRunQ, historicalRunQ)
  updateEntry(
    agentId: string,
    updates: Partial<Pick<AgentRunEntry, 'result' | 'runId' | 'artifacts' | 'phase' | 'errorMessage'>>,
  ): void;
}

// ============================================================================
// Shared SSE handlers
// ============================================================================

type Get = () => AgentRunState;
type Set = (partial: Partial<AgentRunState> | ((state: AgentRunState) => Partial<AgentRunState>)) => void;

// ============================================================================
// Org partitioning helpers
// ============================================================================

/** One organization's runs. Never `undefined` — an org with no runs reads as empty. */
function orgRuns(state: AgentRunState, orgId: OrgId | null): AgentRunMap {
  return readOrgSlice(state.runsByOrg, orgId, () => new Map());
}

/**
 * Store `next` as `orgId`'s runs, republishing `runs` only when that
 * organization is the one on screen.
 *
 * This is the single place the two views are kept consistent, which is why every
 * write in this file goes through it.
 */
function commitOrgRuns(
  state: AgentRunState,
  orgId: OrgId | null,
  next: AgentRunMap,
): Partial<AgentRunState> {
  const runsByOrg = writeOrgSlice(state.runsByOrg, orgId, next);
  return isActiveOrg(orgId) || (orgId === null && getActiveOrgId() === null)
    ? { runsByOrg, runs: next }
    : { runsByOrg };
}

/** Read-modify-write one entry in `orgId`'s slice. Returns unchanged state if the entry is gone. */
function updateOrgEntry(
  state: AgentRunState,
  orgId: OrgId | null,
  agentId: string,
  update: (entry: AgentRunEntry) => AgentRunEntry,
): Partial<AgentRunState> | AgentRunState {
  const current = orgRuns(state, orgId);
  const entry = current.get(agentId);
  if (!entry) return state;
  const next = new Map(current);
  next.set(agentId, update(entry));
  return commitOrgRuns(state, orgId, next);
}

// The SSE onEvent/onError reducer, shared by startRun (POST — new run) and
// reconnectRun (GET — live reconnect on refresh) so both drive identical state.
//
// `orgId` is captured by the caller at run-start time and closed over here, so
// every frame this reducer ever sees is filed against the organization the run
// was started in — including frames that arrive after the user has moved on.
function makeAgentStreamHandlers(
  get: Get,
  set: Set,
  orgId: OrgId | null,
  agentId: string,
  agentName: string,
) {
  const runsOf = () => orgRuns(get(), orgId);

  return {
    onClose() {
      // SSE stream ended without a 'done' event (proxy timeout, network drop, etc.).
      // If the run already started on the server (runId present), transition to
      // 'done' so the persistedRunQ effect in AgentRunTab loads the result from the
      // DB — preventing the phase from being stuck at 'running' forever and causing
      // users to manually Stop + re-run (which creates duplicate backend runs).
      // Correct for BOTH startRun and reconnectRun: a dropped live stream → load
      // the run from the DB rather than leaving the UI stuck.
      const entry = runsOf().get(agentId);
      if (!entry || entry.phase !== 'running') return;
      const newRuns = new Map(runsOf());
      if (entry.runId) {
        newRuns.set(agentId, { ...entry, phase: 'done' });
        void getQueryClient().invalidateQueries({ queryKey: ['agent'] });
        void getQueryClient().invalidateQueries({ queryKey: ['agent-runs'] });
        if (entry.activityId) {
          useActivityStore.getState().completeActivity(entry.activityId, `${agentName} complete`);
          setTimeout(() => useActivityStore.getState().removeActivity(entry.activityId ?? ''), 3000);
        }
      } else {
        // Run never started — reset to configure
        newRuns.set(agentId, { ...entry, phase: 'configure', handle: null });
        if (entry.activityId) {
          useActivityStore.getState().failActivity(entry.activityId, `${agentName} failed to start`);
          setTimeout(() => useActivityStore.getState().removeActivity(entry.activityId ?? ''), 5000);
        }
      }
      set((state) => commitOrgRuns(state, orgId, newRuns));
    },

    onEvent(event: RunEvent) {
      const entry = runsOf().get(agentId);
      if (!entry) return;

      let { events, result, runId, artifacts, phase } = entry;
      let errorMessage = entry.errorMessage ?? null;
      let isDone = false;
      let isError = false;

      if (PROGRESS_EVENT_TYPES.has(event.event)) {
        events = [...events, event].slice(-MAX_VISIBLE_EVENTS);
      }
      if (event.event === 'started' && typeof event.run_id === 'string') {
        runId = event.run_id;
        // User hit Stop during startup (before this id existed). Honor it now:
        // tell the backend to stop, disconnect, and reset — otherwise the run
        // would keep executing with nobody watching.
        if (entry.stopRequested) {
          stopAgentRunOnServer(runId);
          entry.handle?.abort();
          if (entry.activityId) {
            useActivityStore.getState().removeActivity(entry.activityId);
          }
          const stopped = new Map(runsOf());
          stopped.set(agentId, {
            ...entry,
            phase: 'configure',
            events: [],
            result: '',
            runId: null,
            artifacts: [],
            handle: null,
            activityId: null,
            stopRequested: false,
          });
          set((state) => commitOrgRuns(state, orgId, stopped));
          return;
        }
      }
      if (event.event === 'render') {
        result = String(event.markdown ?? event.content ?? '');
      }
      if (event.event === 'artifact' && typeof event.name === 'string') {
        const name = event.name;
        artifacts = [
          ...artifacts.filter((a) => a.name !== name),
          {
            name,
            content_type: String(event.content_type ?? 'application/octet-stream'),
            size_bytes: Number(event.size_bytes ?? 0),
            created_at: new Date().toISOString(),
          },
        ];
      }
      let usage = entry.usage;
      if (event.event === 'done') {
        phase = 'done';
        isDone = true;
        if (typeof event.run_id === 'string') runId = event.run_id;
        if (typeof event.total_tokens === 'number') {
          usage = {
            cost_usd: Number(event.cost_usd ?? 0),
            input_tokens: Number(event.input_tokens ?? 0),
            output_tokens: Number(event.output_tokens ?? 0),
            total_tokens: Number(event.total_tokens ?? 0),
            llm_calls: Number(event.llm_calls ?? 0),
          };
        }
      }
      if (event.event === 'error') {
        phase = 'done';
        isError = true;
        // The frame's `message` IS the reason — keep it. Without a message there
        // is still a failure to report, so fall back to something renderable
        // rather than leaving an errored run with a blank explanation.
        errorMessage =
          typeof event.message === 'string' && event.message
            ? event.message
            : 'The run failed without reporting a reason.';
      }

      // Persist updated run state
      const newRuns = new Map(runsOf());
      newRuns.set(agentId, { ...entry, events, result, runId, artifacts, phase, usage, errorMessage });
      set((state) => commitOrgRuns(state, orgId, newRuns));

      // Invalidate run history as soon as the run finishes.
      // "agent" covers the workspace's useAgent() which bundles runs inside it.
      // "agent-runs" covers the standalone useAgentRuns() list queries.
      if (isDone) {
        void getQueryClient().invalidateQueries({ queryKey: ["agent"] });
        void getQueryClient().invalidateQueries({ queryKey: ["agent-runs"] });
        // Also refresh the run-DETAIL query (persistedRunQ in the run tab). Without
        // this it can stay cached-empty for staleTime (30s) if it fetched mid-run,
        // so the after-run report shows no artifacts/steps while the Reports page
        // (mounted fresh) does. Prefix-match invalidates the just-finished run.
        void getQueryClient().invalidateQueries({ queryKey: ["agent-run"] });
      }

      // Update activity store outside set()
      const { activityId } = entry;
      if (activityId) {
        if (isDone) {
          useActivityStore.getState().completeActivity(activityId, `${agentName} complete`);
          setTimeout(() => useActivityStore.getState().removeActivity(activityId), 3000);
        } else if (isError) {
          useActivityStore.getState().failActivity(activityId, `${agentName} failed`);
          setTimeout(() => useActivityStore.getState().removeActivity(activityId), 5000);
        } else {
          const progress = progressFromEvents(events);
          useActivityStore.getState().updateActivity(activityId, {
            progress: progress.percent,
            indeterminate: progress.indeterminate,
            completedSteps: progress.completed,
            message: `Running ${agentName}…`,
          });
        }
      }
    },

    onError(err: unknown) {
      const entry = runsOf().get(agentId);
      if (!entry) return;
      const { activityId } = entry;
      // The transport failed. sse.ts usually emits an `error` FRAME first, whose
      // message is more specific than anything reconstructable here — so only
      // fill in when the reducer has nothing yet.
      const transportMessage =
        err instanceof Error && err.message
          ? err.message
          : 'The connection to the run was lost.';
      const newRuns = new Map(runsOf());
      newRuns.set(agentId, {
        ...entry,
        phase: 'done',
        errorMessage: entry.errorMessage || transportMessage,
      });
      set((state) => commitOrgRuns(state, orgId, newRuns));
      if (activityId) {
        useActivityStore.getState().failActivity(activityId, `${agentName} failed`);
        setTimeout(() => useActivityStore.getState().removeActivity(activityId), 5000);
      }
    },
  };
}

// ============================================================================
// Store
// ============================================================================

export const useAgentRunStore = create<AgentRunState>()(
  devtools(
    (set, get) => ({
      runsByOrg: new Map(),
      runs: new Map(),

      ensureEntry(agentId, agentName) {
        const orgId = getActiveOrgId();
        if (orgRuns(get(), orgId).has(agentId)) return;
        set((state) => {
          const newRuns = new Map(orgRuns(state, orgId));
          newRuns.set(agentId, {
            agentId,
            agentName,
            phase: 'configure',
            events: [],
            result: '',
            runId: null,
            artifacts: [],
            handle: null,
            activityId: null,
          });
          return commitOrgRuns(state, orgId, newRuns);
        });
      },

      startRun(agentId, agentName, input, scope) {
        // The run belongs to the organization on screen when the user pressed
        // Run. Everything downstream — the entry, the activity, every SSE frame —
        // is filed against this id, not against whatever is selected later.
        const orgId = getActiveOrgId();
        const existing = orgRuns(get(), orgId).get(agentId);

        // Abort any in-flight SSE stream
        if (existing?.handle) existing.handle.abort();

        // Remove stale activity if any
        if (existing?.activityId) {
          useActivityStore.getState().removeActivity(existing.activityId);
        }

        const activityId = `agent_run_${agentId}_${Date.now()}`;

        useActivityStore.getState().startActivity(
          activityId,
          'agent_run',
          `Running ${agentName}...`,
          { agentId, agentName },
        );

        // Initialise entry — handle set after stream created below
        set((state) => {
          const newRuns = new Map(orgRuns(state, orgId));
          newRuns.set(agentId, {
            agentId,
            agentName,
            phase: 'running',
            events: [],
            result: '',
            runId: null,
            artifacts: [],
            handle: null,
            activityId,
          });
          return commitOrgRuns(state, orgId, newRuns);
        });

        const handle = streamAgentRun(
          agentId, input, scope,
          makeAgentStreamHandlers(get, set, orgId, agentId, agentName),
        );

        // Store the handle so it survives React unmounts
        set((state) => updateOrgEntry(state, orgId, agentId, (entry) => ({ ...entry, handle })));
      },

      reconnectRun(agentId, agentName, runId) {
        const orgId = getActiveOrgId();
        const existing = orgRuns(get(), orgId).get(agentId);
        // Already streaming this same run (live handle attached) — don't double-connect.
        if (existing?.handle && existing.runId === runId && existing.phase === 'running') return;
        // Tear down any stale handle before reconnecting.
        if (existing?.handle) existing.handle.abort();

        // Reattach the activity indicator if this resume has none (a hard refresh
        // wipes activityStore) — the run IS live, so the header ActivityDropdown
        // and list badges should show it again. Keyed by runId (not Date.now())
        // so a repeated resume of the same run reuses one activity entry.
        let activityId = existing?.activityId ?? null;
        if (!activityId) {
          activityId = `agent_run_${agentId}_${runId}`;
          useActivityStore.getState().startActivity(
            activityId,
            'agent_run',
            `Running ${agentName}...`,
            { agentId, agentName },
          );
        }

        // Seed the entry as a live run: phase 'running' + known runId so Stop works
        // and events reduce correctly.
        set((state) => {
          const newRuns = new Map(orgRuns(state, orgId));
          newRuns.set(agentId, {
            agentId,
            agentName,
            phase: 'running',
            events: existing?.events ?? [],
            result: existing?.result ?? '',
            runId,
            artifacts: existing?.artifacts ?? [],
            handle: null,
            activityId,
            usage: existing?.usage ?? null,
          });
          return commitOrgRuns(state, orgId, newRuns);
        });

        const handle = reconnectAgentRun(
          runId,
          makeAgentStreamHandlers(get, set, orgId, agentId, agentName),
        );

        set((state) => updateOrgEntry(state, orgId, agentId, (entry) => ({ ...entry, handle })));
      },

      stopRun(agentId) {
        const orgId = getActiveOrgId();
        const entry = orgRuns(get(), orgId).get(agentId);
        if (!entry) return;

        // The backend can only stop the run if we hand it the run_id — and that id
        // arrives via the SSE 'started' event. If we don't have it yet, aborting the
        // stream now would ORPHAN the backend run: it keeps executing and there's no
        // id left to cancel it with (the old code aborted + reset regardless, so an
        // early Stop silently did nothing server-side). So: no id yet → flag the
        // intent and KEEP the stream alive; the 'started' handler fires the stop the
        // instant the id lands. Have the id → stop, then disconnect + reset.
        if (!entry.runId) {
          set((state) =>
            updateOrgEntry(state, orgId, agentId, (e) => ({ ...e, stopRequested: true })),
          );
          return;
        }

        stopAgentRunOnServer(entry.runId);
        entry.handle?.abort();

        if (entry.activityId) {
          useActivityStore.getState().removeActivity(entry.activityId);
        }

        set((state) =>
          updateOrgEntry(state, orgId, agentId, (e) => ({
            ...e,
            phase: 'configure',
            events: [],
            result: '',
            runId: null,
            artifacts: [],
            handle: null,
            activityId: null,
            stopRequested: false,
          })),
        );
      },

      setPhase(agentId, phase) {
        const orgId = getActiveOrgId();
        set((state) => updateOrgEntry(state, orgId, agentId, (entry) => ({ ...entry, phase })));
      },

      setEvents(agentId, events) {
        const orgId = getActiveOrgId();
        set((state) => updateOrgEntry(state, orgId, agentId, (entry) => ({ ...entry, events })));
      },

      updateEntry(agentId, updates) {
        const orgId = getActiveOrgId();
        set((state) => updateOrgEntry(state, orgId, agentId, (entry) => ({ ...entry, ...updates })));
      },
    }),
    { name: 'agent-run-store' }
  )
);

// ============================================================================
// Organization scoping
// ============================================================================

/**
 * Re-point `runs` at the newly active organization's slice.
 *
 * Nothing is aborted and nothing is discarded: the run is executing on the
 * server regardless of what the browser is showing, and killing the stream on a
 * switch would lose the transcript of a run the user is still paying for. The
 * entries stay in `runsByOrg`, still receiving frames, and reappear intact when
 * the user switches back.
 */
registerOrgScopedState({
  name: 'agentRunStore',
  onOrgChange: (nextOrgId) => {
    useAgentRunStore.setState((state) => ({
      runs: orgRuns(state, nextOrgId),
    }));
  },
  onSignOut: () => {
    // Abort here, unlike on a switch: nobody is coming back for these, and a
    // stream left open would keep writing into state the next sign-in reads.
    for (const runs of useAgentRunStore.getState().runsByOrg.values()) {
      for (const entry of runs.values()) entry.handle?.abort();
    }
    useAgentRunStore.setState({ runsByOrg: new Map(), runs: new Map() });
  },
});
